import React from "react";
import InputField from "../../common/InputField";
import { IoMdTrash } from "react-icons/io";
import { notify } from "../../common/Toast";
import { aging } from "../../utlis/apiService";
import { FormFooter } from "../../common/Shared";
import Model from "../../common/Model";

const AgingForm = ({
  data,
  onSubmit,
  onClose,
  setFormData,
  formData,
  onMarkDone,
  isSaving = false,
}) => {
  const rawStatus = formData?.status ?? data?.status ?? "";
  const isStatusDone =
    typeof rawStatus === "string" && rawStatus.toLowerCase() === "done";
  const today = new Date().toISOString()?.split("T")[0];

  const isGasInvalid =
    formData.end_Gas_Reading !== "" &&
    formData.end_Gas_Reading != null &&
    Number(formData.start_Gas_Reading) >= Number(formData.end_Gas_Reading);


  const parseTimeToMinutes = (t) => {
    if (!t) return null;
    const [h = 0, m = 0] = String(t).split(":").map(Number);
    return h * 60 + m;
  };

  const parseDurationToMinutes = (v) => {
    if (v === "" || v == null) return null;
    const s = String(v).trim();
    if (s.includes(":")) {
      const [h = 0, m = 0] = s.split(":").map(Number);
      return h * 60 + m;
    }
    const n = Number(s);
    return Number.isFinite(n) ? Math.round(n * 60) : null;
  };

  const minutesToHHMM = (mins) => {
    const m = ((mins % 1440) + 1440) % 1440;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return String(h).padStart(2, "0") + ":" + String(mm).padStart(2, "0");
  };

  const handleTimeChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      const startMins = parseTimeToMinutes(updated.in_Time);
      const durMins = parseDurationToMinutes(updated.out_Time);

      if (startMins != null && durMins != null) {
        updated.total_Time = minutesToHHMM(startMins + durMins);
      } else {
        updated.total_Time = "";
      }
      return updated;
    });
  };

  const updateDetailField = (index, field, value) => {
    const updated = [...formData.agingDetails];
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, agingDetails: updated }));
  };

  const handleDeleteDetail = async (index, seq_No) => {
    if (window.confirm("Are you sure you want to delete this row?")) {
      const updated = [...formData.agingDetails];
      updated.splice(index, 1);
      setFormData((prev) => ({ ...prev, agingDetails: updated }));

      if (seq_No > 0) {
        try {
          await aging.agingDetailsDelete(seq_No);
          notify("Detail deleted successfully", "success");
        } catch (error) {
          notify("Failed to delete detail", "error");
        }
      }
    }
  };

  const primaryBtnClass = isStatusDone
    ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
    : "bg-blue-600 hover:bg-blue-700 text-white  active:scale-95 transition-all";

  return (
    <Model
      isOpen={true}
      onClose={onClose}
      name="Aging Details"
      widthClass="max-w-6xl"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isGasInvalid) return;
          onSubmit();
        }}
        className="flex flex-col"
      >
        {/* Core Registry */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-6">
          <InputField
            label="Aging No"
            value={data.aging_No || ""}
            disabled
            className="bg-gray-50 text-gray-400 rounded-none border-gray-200"
          />
          <InputField
            type="date"
            label="Aging Date"
            value={formData.aging_Date || today}
            onChange={(val) =>
              setFormData((p) => ({ ...p, aging_Date: val }))
            }
            className="rounded-none border-gray-200 focus:border-primary"
          />
          <InputField
            label="Shift"
            value={formData.shift || ""}
            onChange={(val) => setFormData((p) => ({ ...p, shift: val }))}
            className="rounded-none border-gray-200 focus:border-primary"
          />
          <InputField
            label="Temperature"
            type="number"
            value={formData.temperature || ""}
            onChange={(val) =>
              setFormData((p) => ({ ...p, temperature: val }))
            }
            className="rounded-none border-gray-200 focus:border-primary"
          />
        </div>

        {/* Industrial Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-6">
          {/* Gas Metrics */}
          <div className="relative group rounded-none">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <InputField
                label="Start Gas Reading"
                value={formData.start_Gas_Reading}
                onChange={(val) =>
                  setFormData((p) => ({ ...p, start_Gas_Reading: val }))
                }
                className="rounded-none"
              />
              <InputField
                label="End Gas Reading"
                value={formData.end_Gas_Reading}
                onChange={(val) =>
                  setFormData((p) => ({ ...p, end_Gas_Reading: val }))
                }
                className="rounded-none"
                error={isGasInvalid ? "Must be greater than start reading" : null}
              />
            </div>
            <div className="p-4 bg-primary/5 flex justify-between items-center border border-primary/10 rounded-none">
              <span className="text-[10px] font-black text-primary uppercase tracking-[2px]">
                Total Consumption
              </span>
              <span className="text-[18px] text-primary font-black leading-none">
                {(Number(formData.end_Gas_Reading) || 0) -
                  (Number(formData.start_Gas_Reading) || 0)} <span className="text-[10px] text-primary/70 ml-1">UNIT</span>
              </span>
            </div>
          </div>

          {/* Timing Metrics */}
          <div className="relative group rounded-none">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <InputField
                type="time"
                label="In Time (Start)"
                value={formData.in_Time || ""}
                onChange={(val) => handleTimeChange("in_Time", val)}
                className="rounded-none font-plus"
              />
              <InputField
                type="number"
                label="Cycle Duration (Hrs)"
                value={formData.out_Time || ""}
                onChange={(val) => handleTimeChange("out_Time", val)}
                className="rounded-none font-plus"
              />
            </div>
            <div className="p-4 bg-primary/5 flex justify-between items-center border border-primary/10 rounded-none">
              <span className="text-[10px] font-black text-primary uppercase tracking-[2px]">
                EST. FINISH TIME
              </span>
              <span className="text-[18px] text-primary font-black leading-none">
                {formData.total_Time || "--:--"} <span className="text-[10px] text-primary/70 ml-1">HR</span>
              </span>
            </div>
          </div>
        </div>

        {/* Batch Registry */}
        <div className="bg-white border-b border-slate-100 overflow-hidden rounded-none">
          <div className="bg-gray-50 px-5 py-3 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-[2px]">
              Batch Production Details
            </h3>
            <span className="text-[10px] bg-primary/10 text-primary px-3 py-1 font-black tracking-widest uppercase border border-primary/20">
              {(formData?.agingDetails || []).length} ROWS
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white border-b border-gray-200">
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-[2px]">Batch ID</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-[2px]">Die Number</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-[2px]">Order No</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-[2px] text-center w-24">Cut Length</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-[2px] text-center w-24">Appr Pcs</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-[2px] w-32">Qty</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-[2px] w-32">Hardness</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-[2px]">Remark</th>
                  <th className="px-5 py-3 text-[10px] font-black text-gray-400 uppercase tracking-[2px] text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {formData?.agingDetails?.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3 text-[12px] font-black text-gray-900">{row.batch_Id}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col">
                        <span className="text-[12px] font-black text-primary uppercase tracking-wider">{row.dieNumber || "N/A"}</span>
                        <span className="text-[9px] text-gray-400 uppercase tracking-tighter">ID: {row.die_Id}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[12px] text-gray-600 font-medium">{row.po_No}</td>
                    <td className="px-5 py-3 text-center text-[12px] font-mono text-gray-400">{row.cut_Length}</td>
                    <td className="px-5 py-3 text-center text-[12px] font-black text-gray-900">{row.approved_Pcs}</td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        className="w-full bg-gray-50 border border-gray-200 px-3 py-1.5 text-[12px] font-black text-center focus:border-primary transition-all rounded-none"
                        value={row.quantity ?? ""}
                        onChange={(e) => updateDetailField(idx, "quantity", e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        className="w-full bg-gray-50 border border-gray-200 px-3 py-1.5 text-[12px] font-black text-center focus:border-primary transition-all rounded-none"
                        value={row.hardness ?? ""}
                        onChange={(e) => updateDetailField(idx, "hardness", e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        className="w-full bg-gray-50 border border-gray-200 px-4 py-1.5 text-[11px] font-medium focus:border-primary transition-all rounded-none"
                        placeholder="Add remark..."
                        value={row.remark ?? ""}
                        onChange={(e) => updateDetailField(idx, "remark", e.target.value)}
                      />
                    </td>
                    <td className="px-5 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => handleDeleteDetail(idx, row.seq_No)}
                        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all border border-transparent hover:border-red-100 rounded-none"
                      >
                        <IoMdTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {(!formData?.agingDetails || formData.agingDetails.length === 0) && (
                  <tr>
                    <td colSpan={9} className="p-12 text-center text-[11px] font-black text-gray-400 uppercase tracking-[3px] bg-gray-50/30">
                      No production details available for this aging cycle.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <FormFooter
          onCancel={onClose}
          onSubmit={isStatusDone ? onClose : onSubmit}
          submitText={isStatusDone ? "Close" : "Save"}
          isSaving={isSaving}
        >
          {!isStatusDone && (
            <button
              type="button"
              onClick={() => {
                if (isGasInvalid) return;
                onMarkDone();
              }}
              disabled={isSaving || isGasInvalid}
              className="px-8 py-2 border border-amber-500 text-amber-600 hover:bg-amber-50 font-black text-[10px] uppercase tracking-[2.5px] transition-all disabled:opacity-50"
            >
              Mark Done
            </button>
          )}
        </FormFooter>
      </form>
    </Model>
  );
};

export default AgingForm;
